import { z } from 'zod';
import { DIAGRAM_MAX_ELEMENTS, DIAGRAM_PITCH, DIAGRAM_PITCH_HEIGHT, checkDiagramArrows, diagramElementSchema, roundCoordinate, } from './training.js';
/**
 * Bausteine fuer die Skizze: fertige Aufbauten (im Code, siehe `training-setups.ts`) und eigene Bausteine
 * des Vereins (Tabelle `training_blocks`).
 *
 * Ein Baustein ist eine Liste von Skizzen-Elementen, die auf ein beliebiges Feld eingefuegt wird. Die Elemente
 * liegen relativ zu ihrem Umriss: die linke obere Ecke des Umrisses ist (0, 0). `pitch` nennt das Feld, auf dem
 * der Baustein entstanden ist - Prozentwerte der Hoehe gelten fuer dessen Hoehe, beim Einfuegen auf ein anderes
 * Feld rechnet der Editor sie in Zeichnungseinheiten um, damit die Form nicht verzerrt.
 */
const requiredText = (max) => z.string().trim().min(1, 'Pflichtfeld').max(max);
export const BLOCK_LIMITS = {
    name: 60,
    /** Hoechstzahl eigener (nicht archivierter) Bausteine je Verein. */
    perClub: 200,
};
const blockElements = z.array(diagramElementSchema).min(1, 'Mindestens ein Element').max(DIAGRAM_MAX_ELEMENTS, `Höchstens ${DIAGRAM_MAX_ELEMENTS} Elemente`);
/** Eigenen Baustein anlegen. Der Server legt die Elemente relativ zum Umriss ab (`normalizeBlockElements`). */
export const blockInputSchema = z
    .object({
    name: requiredText(BLOCK_LIMITS.name),
    pitch: z.enum(DIAGRAM_PITCH),
    elements: blockElements,
})
    .superRefine(checkDiagramArrows);
/** Umbenennen: mehr laesst sich an einem Baustein nicht aendern. */
export const blockUpdateSchema = z.object({ name: requiredText(BLOCK_LIMITS.name) });
/**
 * Wendet `map` auf jeden Punkt eines Elements an: Ort, Anfang, Ende, Kontrollpunkt und Knickpunkte; bei einer Zone
 * auf die vier Ecken (das neue Rechteck ist ihr Umriss). Alles andere (Drehung, Blickrichtung, Beschriftung ...) bleibt.
 * `map` rundet selbst auf das Raster der Koordinaten, wenn es muss.
 */
export function mapElementPoints(element, map) {
    const at = (point) => map(point[0], point[1]);
    switch (element.type) {
        case 'arrow': {
            const next = { ...element, from: at(element.from), to: at(element.to) };
            if (element.via)
                next.via = at(element.via);
            if (element.points)
                next.points = element.points.map(at);
            return next;
        }
        case 'measure':
            return { ...element, from: at(element.from), to: at(element.to) };
        case 'zone': {
            const corners = [map(element.x, element.y), map(element.x + element.w, element.y), map(element.x, element.y + element.h), map(element.x + element.w, element.y + element.h)];
            const xs = corners.map((corner) => corner[0]);
            const ys = corners.map((corner) => corner[1]);
            const x = Math.min(...xs);
            const y = Math.min(...ys);
            return { ...element, x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
        }
        default: {
            const [x, y] = map(element.x, element.y);
            return { ...element, x, y };
        }
    }
}
/** Alle Punkte eines Elements, die zu seinem Umriss zaehlen (bei Kurven samt Kontrollpunkt, bei Zonen die Ecken). */
function outlinePoints(element) {
    switch (element.type) {
        case 'arrow':
            return [element.from, element.to, ...(element.via ? [element.via] : []), ...(element.points ?? [])];
        case 'measure':
            return [element.from, element.to];
        case 'zone':
            return [[element.x, element.y], [element.x + element.w, element.y + element.h]];
        default:
            return [[element.x, element.y]];
    }
}
/** Umriss einer Liste von Elementen in Feldprozent; `null` ohne Elemente. */
export function elementsOutline(elements) {
    const points = elements.flatMap(outlinePoints);
    if (points.length === 0)
        return null;
    return {
        minX: Math.min(...points.map((point) => point[0])),
        minY: Math.min(...points.map((point) => point[1])),
        maxX: Math.max(...points.map((point) => point[0])),
        maxY: Math.max(...points.map((point) => point[1])),
    };
}
/** Verschiebt alle Elemente um (dx, dy) - ohne Klemmen; die Summe muss wieder im Feld liegen, das prueft der Aufrufer. */
export function shiftElements(elements, dx, dy) {
    if (dx === 0 && dy === 0)
        return [...elements];
    return elements.map((element) => mapElementPoints(element, (x, y) => [roundCoordinate(x + dx), roundCoordinate(y + dy)]));
}
/** Legt die Elemente relativ zu ihrem Umriss ab: dessen linke obere Ecke wird (0, 0). */
export function normalizeBlockElements(elements) {
    const box = elementsOutline(elements);
    if (!box)
        return [];
    return shiftElements(elements, -box.minX, -box.minY);
}
/**
 * Rechnet Elemente von einem Feld auf ein anderes um: x bleibt, y (Prozent der Hoehe) wird so umgerechnet,
 * dass der Abstand in Zeichnungseinheiten gleich bleibt. Auf das Raster der Koordinaten gerundet.
 */
export function rescaleElementsToPitch(elements, from, to) {
    if (from === to)
        return [...elements];
    const factor = DIAGRAM_PITCH_HEIGHT[from] / DIAGRAM_PITCH_HEIGHT[to];
    return elements.map((element) => mapElementPoints(element, (x, y) => [x, roundCoordinate(y * factor)]));
}
//# sourceMappingURL=training-block.js.map